@if (incidentId > 0) {
  <button nz-button nzType="default" nzSize="large" [nzBlock]="mobile" class="chat-launch" (click)="open()">
    <nz-icon [nzType]="hasEscalation ? 'exclamation-circle' : 'message'" [class.escalation-icon]="hasEscalation" /> Chat
    @if (!visible && unreadCount) {
      <nz-badge class="chat-unread" [nzCount]="unreadCount" [attr.aria-label]="unreadCount + ' unread messages'" role="status" />
    }
    @if (pending.length) { <span>({{ pending.length }} pending)</span> }
  </button>
  @if (mobile && notificationsEnabled && push.configured) {
    <div class="chat-notifications">
      @if (push.needsInstallation) {
        <button nz-button nzType="link" (click)="push.showPrompt = true"><nz-icon nzType="bell" /> Set up iPhone notifications</button>
        <small>Open this dashboard from your Home Screen to receive notifications.</small>
      } @else if (!push.supported) {
        <small role="status">Chat notifications are unavailable in this browser.</small>
      } @else if (push.permission === 'denied') {
        <small role="status">{{ push.dashboard.isIOS ? 'Chat notifications are blocked. Open iPhone Settings, then Notifications, and allow notifications for Truck Breakdown.' : 'Chat notifications are blocked. Allow notifications in this site\'s browser settings.' }}</small>
      } @else if (push.enabled) {
        <small class="notifications-enabled" role="status"><nz-icon nzType="bell" /> Chat notifications enabled</small>
      } @else {
        <button nz-button nzType="link" [nzLoading]="push.busy" [disabled]="push.busy" (click)="push.register(true)">
          <nz-icon nzType="bell" /> {{ push.busy ? 'Enabling notifications…' : push.notice ? 'Retry chat notifications' : 'Enable chat notifications' }}
        </button>
        @if (push.notice) { <small role="status">{{ push.notice }}</small> }
      }
    </div>
  }
}
<nz-modal [nzVisible]="push.showPrompt" [nzTitle]="push.needsInstallation ? 'Set up iPhone notifications' : 'Get chat notifications'" [nzFooter]="notificationFooter"
  (nzOnCancel)="push.showPrompt = false" [nzWidth]="400">
  <ng-container *nzModalContent>
    @if (push.needsInstallation) {
      <ol>
        <li>Open this breakdown in Safari. If you are inside WhatsApp, use its menu to open the link in Safari first.</li>
        <li>In Safari, tap Share, then Add to Home Screen. Keep Open as Web App enabled if that option appears.</li>
        <li>Open Truck Breakdown from your Home Screen and tap Enable chat notifications, then Allow.</li>
      </ol>
      <p>Already added it? Copy this link, open Truck Breakdown from your Home Screen, then choose Open another breakdown. Use the same app for future incidents.</p>
      <label for="iphone-breakdown-link">This breakdown link</label>
      <input id="iphone-breakdown-link" nz-input readonly [value]="push.dashboardLink" (focus)="$any($event.target).select()" />
      <button nz-button (click)="push.copyDashboardLink()">Copy breakdown link</button>
      @if (push.copyNotice) { <p role="status">{{ push.copyNotice }}</p> }
    } @else {
      <p>Get notified about new breakdown chat messages, even when this dashboard is closed or your device is locked.</p>
      <p>Your browser will ask you to allow notifications. You only need to allow them once for this installed app or browser.</p>
    }
  </ng-container>
  <ng-template #notificationFooter>
    <button nz-button (click)="push.showPrompt = false">Not now</button>
    @if (!push.needsInstallation) {
      <button nz-button nzType="primary" (click)="push.register(true)"><nz-icon nzType="bell" /> Enable notifications</button>
    }
  </ng-template>
</nz-modal>
<nz-drawer [nzVisible]="visible" [nzWidth]="mobile ? '100%' : '520px'" [nzMask]="mobile"
  [nzTitle]="'Chat · Incident #' + (incidentNumber || incidentId)" (nzOnClose)="close()"
  [nzBodyStyle]="{ display: 'flex', flexDirection: 'column', padding: '12px', overflow: 'hidden' }">
  <ng-container *nzDrawerContent>
    <div class="chat-toolbar">
      <span>Sending as {{ senderNames[senderType] }}</span>
      @if (!mobile && messages.length) {
        <button nz-button class="toolbar-action" [disabled]="forwardBusy" (click)="selecting ? resetSelection() : selecting = true">
          <nz-icon [nzType]="selecting ? 'close' : 'select'" /> {{ selecting ? 'Done' : 'Select messages' }}
        </button>
      }
      <button nz-button class="toolbar-action" (click)="refresh()" [nzLoading]="loading" [disabled]="loadingOlder">
        <nz-icon nzType="reload" /> Refresh
      </button>
    </div>
    @if (!mobile && selecting) {
      <div class="forward-actions" aria-label="Share selected messages">
        <strong>{{ selectedIds.size }} selected</strong>
        <small>Select saved messages, including your own.</small>
        <button nz-button nzType="primary" [nzLoading]="forwarding" [disabled]="forwardBusy || !selectedIds.size" (click)="sendSelectedToSelf()">
          <nz-icon nzType="whats-app" /> Send to my WhatsApp
        </button>
        <button nz-button [nzLoading]="preparingShare || sharing" [disabled]="forwardBusy || !selectedIds.size || !shareSupported" (click)="shareSelected()">
          <nz-icon nzType="share-alt" /> {{ shareReady ? 'Share now' : 'System share' }}
        </button>
        @if (!shareSupported) { <small>System sharing is unavailable in this browser. Use Send to my WhatsApp.</small> }
      </div>
    }
    @if (forwardNotice) { <p class="forward-notice" role="status">{{ forwardNotice }}</p> }
    @if (forwardError) { <p class="error" role="alert">{{ forwardError }}</p> }
    @if (historyNotice) { <p class="notice" role="status">{{ historyNotice }}</p> }
    @if (readStateNotice) { <p class="notice" role="status">{{ readStateNotice }}</p> }
    <div #messageList class="chat-history" aria-label="Incident chat messages">
      @if (hasMore) {
        <button nz-button class="toolbar-action" (click)="older()" [nzLoading]="loadingOlder" [disabled]="loading">
          <nz-icon nzType="history" /> Load older messages
        </button>
      }
      @if (!messages.length && !pending.length && !loading) { <p class="empty">No messages yet.</p> }
      @for (message of messages; track message.chatMessageId) {
        <article class="message" [class.own]="message.chatSenderTypeId === senderType" [class.selected]="selectedIds.has(message.chatMessageId)"
          [class.escalated]="!!message.escalatedDate" [class.highlighted]="message.chatMessageId === highlightedMessageId"
          [attr.data-chat-message-id]="message.chatMessageId">
          @if (!mobile && selecting) {
            <label nz-checkbox class="message-select" [ngModel]="selectedIds.has(message.chatMessageId)" [nzDisabled]="forwardBusy"
              (ngModelChange)="toggleSelection(message, $event)" [attr.aria-label]="'Select message from ' + senderNames[message.chatSenderTypeId]">Select</label>
          }
          <header><strong>{{ senderNames[message.chatSenderTypeId] }}</strong><time>{{ message.createdDate | date:'dd MMM HH:mm' }}</time></header>
          @if (message.escalatedDate) {
            <small class="escalation-status"><nz-icon nzType="exclamation-circle" />
              Escalated {{ message.escalatedDate | date:'dd MMM HH:mm' }}
              @if (message.escalationAcknowledgedDate) { · Viewed by callcentre {{ message.escalationAcknowledgedDate | date:'dd MMM HH:mm' }} }
            </small>
          }
          @if (message.message) { <p>{{ message.message }}</p> }
          @if (message.document; as document) {
            @switch (mediaInfo(document).kind) {
              @case ('image') { <a [href]="document.url" target="_blank" rel="noopener noreferrer"><img [src]="document.url" [alt]="document.description || 'Chat image'" loading="lazy" /></a> }
              @case ('audio') { <audio [src]="document.url" controls preload="none"></audio> }
              @case ('video') { <video [src]="document.url" controls playsinline preload="none"></video> }
            }
            <a class="attachment-link" [href]="document.url" target="_blank" rel="noopener noreferrer">Open {{ document.description || 'attachment' }}</a>
          }
          @if (senderType === 3 && message.chatSenderTypeId === 3 && !message.escalatedDate) {
            <button nz-button nzSize="small" nzDanger [nzLoading]="escalatingId === message.chatMessageId"
              [disabled]="escalatingId !== null" (click)="escalate(message)"><nz-icon nzType="exclamation-circle" /> Escalate to callcentre</button>
          }
        </article>
      }
      @for (item of pending; track item.clientMessageId) {
        <article class="message own pending">
          <header><strong>{{ senderNames[item.chatSenderTypeId] }}</strong><span>{{ item.status === 'sending' ? 'Uploading' : item.status === 'failed' ? 'Failed' : 'Pending' }} {{ progress[item.clientMessageId] !== undefined && item.status === 'sending' ? progress[item.clientMessageId] + '%' : '' }}</span></header>
          @if (item.message) { <p>{{ item.message }}</p> }
          @if (item.filename) { <p>{{ item.filename }}</p> }
          @if (item.error) { <p class="error">{{ item.error }}</p> }
          @if (item.status !== 'sending') {
            <button nz-button nzSize="small" (click)="retry(item)"><nz-icon nzType="reload" /> Retry</button>
            <button nz-button nzSize="small" nzDanger (click)="removePending(item)"><nz-icon nzType="delete" /> Remove unsent</button>
          }
        </article>
      }
    </div>
    <div class="chat-composer">
      @if (error) { <p class="error" role="alert">{{ error }}</p> }
      @if (preparing) { <p class="composer-status" role="status">Checking attachments…</p> }
      @if (!composerOpen) {
        <button nz-button class="reply-action" (click)="toggleComposer()" [attr.aria-controls]="composerId" aria-expanded="false">
          <nz-icon nzType="message" /> {{ text.trim() || attachments.length ? 'Continue reply' : 'Reply' }}
        </button>
      }
      <div class="composer-panel" [class.expanded]="composerOpen" [id]="composerId"
        [attr.inert]="composerOpen ? null : ''" [attr.aria-hidden]="!composerOpen">
        <div class="composer-inner">
          <div class="composer-content">
            <div class="attachment-previews">
              @for (attachment of attachments; track attachment.url; let i = $index) {
                <div class="attachment-preview">
                  @if (attachment.kind === 'image') { <img [src]="attachment.url" [alt]="attachment.file.name" /> }
                  @else { <nz-icon class="preview-icon" [nzType]="attachment.kind === 'audio' ? 'audio' : 'video-camera'" /> }
                  <a class="attachment-name" [href]="attachment.url" target="_blank" rel="noopener noreferrer" [title]="attachment.file.name">{{ attachment.file.name }}</a>
                  <button nz-button class="remove-attachment" nzDanger [disabled]="saving" [attr.aria-label]="'Remove ' + attachment.file.name"
                    (click)="removeAttachment(i)"><nz-icon nzType="delete" /></button>
                  @if (attachment.kind === 'audio') { <audio [src]="attachment.url" controls></audio> }
                </div>
              }
            </div>
            <div class="message-entry">
              <textarea #messageInput nz-input [nzAutosize]="{ minRows: 1, maxRows: 3 }" placeholder="Write a message…" aria-label="Message"
                [(ngModel)]="text" (ngModelChange)="remember()" [disabled]="saving"></textarea>
              <button nz-button nzType="primary" class="send-message" (click)="send()" [nzLoading]="saving" aria-label="Send message" title="Send message"
                [disabled]="preparing || recording || requestingMicrophone || (!text.trim() && !attachments.length)">
                @if (!saving) { <nz-icon nzType="send" /> }
              </button>
            </div>
            @if (attachments.length > 1) { <small class="composer-hint">Each attachment sends separately. Text accompanies the first.</small> }
            @if (recording) {
              <div class="recording">
                <span class="recording-status" role="status"><nz-icon nzType="audio" /> {{ recordingSeconds }}s / 2 min</span>
                <button nz-button nzType="primary" nzDanger (click)="stopRecording()" title="Stop and send your voice note"><nz-icon nzType="stop" /> Stop and send</button>
                <button nz-button class="discard-recording" (click)="stopRecording(true)" aria-label="Discard recording" title="Discard recording"><nz-icon nzType="close" /></button>
              </div>
            }
            <div class="attachment-actions" [class.mobile]="mobile" [hidden]="recording">
              <button nz-button class="attachment-action" (click)="remember(); files.click()"
                [title]="mobile ? 'Attach images, audio or video. Audio and video must be two minutes or shorter.' : 'Attach images, audio or video.'"
                [disabled]="saving || preparing || recording || requestingMicrophone">
                <nz-icon nzType="paper-clip" /> <span>Files</span>
              </button>
              <input #files type="file" accept="image/*,audio/*,video/*" multiple hidden (change)="selectFiles($event)" />
              @if (mobile) {
                <button nz-button class="attachment-action" (click)="capture(camera)" title="Take a photo and send it immediately"
                  [disabled]="saving || preparing || recording || requestingMicrophone">
                  <nz-icon nzType="camera" /> <span>Photo</span>
                </button>
                <button nz-button class="attachment-action" (click)="capture(video)" title="Record and send video. Maximum two minutes."
                  [disabled]="saving || preparing || recording || requestingMicrophone">
                  <nz-icon nzType="video-camera" /> <span>Video</span>
                </button>
                <input #camera type="file" accept="image/*" capture="environment" hidden (change)="selectFiles($event, true)" />
                <input #video type="file" accept="video/*" capture="environment" hidden (change)="selectFiles($event, true)" />
              }
              <button nz-button class="attachment-action voice-note-action" (click)="startRecording()" [nzLoading]="requestingMicrophone"
                title="Record voice. Stop and send, or sends automatically after two minutes." [disabled]="saving || preparing || recording">
                @if (!requestingMicrophone) { <nz-icon nzType="audio" /> } <span>Voice</span>
              </button>
              <button nz-button class="collapse-reply" (click)="toggleComposer()" [disabled]="requestingMicrophone"
                [attr.aria-controls]="composerId" aria-expanded="true" aria-label="Collapse reply" title="Collapse reply">
                <nz-icon nzType="down" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>
</nz-drawer>
